/** /notifications/language-aliases — cross-tenant Language_Alias catalog (grid +
 *  create/edit dialog). An alias maps an arbitrary value a policy may return
 *  onto a target ISO 639-1 language code. A null tenant is a system-wide alias.
 *  Filters (operator scope / enabled) and search are held in local state and
 *  passed to the list query as `extra`. */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as aliasesApi from '@/api/languageAliases';
import type { Alias } from '@/api/languageAliases';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import {
  pickedSentinel,
  TenantPicker,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatRelativeTime } from '@/utils/format';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

import { LanguageAliasFormDialog } from './LanguageAliasFormDialog';

/** Sentinel picker option: system-wide aliases (`tour_operator IS NULL`). */
const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System / global (no operator)' };
const SYSTEM_EXTRA_OPTIONS = [SYSTEM_TENANT];

export function LanguageAliasesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_language_aliases_manage');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // The operators (several, D1) or the System sentinel alone (D3); they follow
  // the console scope until picked here.
  const [tenants, setTenants] = useScopedTenantsState();
  const [enabledFilter, setEnabledFilter] = useState<'any' | 'true' | 'false'>('any');

  const tenantIds = formatTenantIds(tenants.map((t) => t.id));
  const tenantParam = pickedSentinel(tenants, SYSTEM_EXTRA_OPTIONS) ? 'system' : tenantIds;

  const anyFilterActive = tenants.length > 0 || enabledFilter !== 'any';
  const resetAllFilters = () => {
    setTenants([]);
    setEnabledFilter('any');
  };

  const extra = useMemo(() => {
    const e: Record<string, string | number | boolean> = {};
    if (tenantParam) e.tour_operator_id = tenantParam;
    if (enabledFilter !== 'any') e.enabled = enabledFilter === 'true';
    return e;
  }, [tenantParam, enabledFilter]);

  const editQuery = useQuery({
    queryKey: ['language_aliases', 'detail', editId],
    queryFn: () => aliasesApi.retrieve(editId as number),
    enabled: editId !== null,
  });

  // The create dialog features the page's operators and pre-selects the one when
  // there is exactly one (D9); the System sentinel (id 0) names no operator.
  const pageTenants = pageTenantsFrom(tenantIds, tenants);

  useCreateIntent(() => openCreate());

  const openCreate = () => {
    setEditId(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((id: number) => {
    setEditId(id);
    setDialogOpen(true);
  }, []);

  const columns = useMemo<GridColDef<Alias>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'alias',
        headerName: 'Alias',
        flex: 1,
        minWidth: 200,
        renderCell: (p) =>
          canManage ? (
            <Box
              component="button"
              type="button"
              onClick={() => openEdit(p.row.id)}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: '#0a1a3f',
                fontWeight: 500,
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              {p.row.alias}
            </Box>
          ) : (
            p.row.alias
          ),
      },
      {
        field: 'language_code',
        headerName: 'Language code',
        width: 160,
        valueGetter: (_, row) => row.language_code ?? '',
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        valueGetter: (_, row) => row.tenant?.name ?? 'System',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} empty="System" />,
      },
      {
        // `enabled` is a filter-only param on the backend — it is NOT in the
        // list endpoint's ordering whitelist (_ORDER_FIELDS), so a header sort
        // would silently fall back to alias. Keep it display-only.
        field: 'enabled',
        headerName: 'Enabled',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (p) =>
          p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 140,
        sortable: true,
        renderCell: (p) => (
          <span title={p.row.updated_ts ?? ''}>{formatRelativeTime(p.row.updated_ts)}</span>
        ),
      },
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      aliasesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'alias',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'scope',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={tenants}
            onChange={setTenants}
            label="Operator"
            extraOptions={SYSTEM_EXTRA_OPTIONS}
          />
        </Box>
      ),
    },
    {
      key: 'enabled',
      priority: 3,
      active: enabledFilter !== 'any',
      node: (
        <TextField
          select
          size="small"
          label="Enabled"
          value={enabledFilter}
          sx={{ width: 160 }}
          onChange={(e) => setEnabledFilter(e.target.value as 'any' | 'true' | 'false')}
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="language-policies"
        title="Language Aliases"
        subtitle="Map an arbitrary value a policy may return onto a target ISO 639-1 language code. A system alias has no owning operator."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New language alias
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<Alias>
        queryKey={['language_aliases']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        batchResource="language_aliases"
        batchActions={[
          deleteBatchAction('console_language_aliases_manage'),
          batchEditAction('language_aliases', 'alias'),
        ]}
        defaultSort={{ field: 'alias', sort: 'asc' }}
        emptyMessage="No language aliases yet."
        headerToolbar={{
          onResetAllFilters: resetAllFilters,
          resetDisabled: !anyFilterActive,
          search: true,
          searchPlaceholder: 'Alias, language code…',
          exportFileName: 'language-aliases',
        }}
      />

      {dialogOpen && (editId === null || editQuery.data) && (
        <LanguageAliasFormDialog
          open={dialogOpen}
          alias={editId === null ? null : (editQuery.data ?? null)}
          pageTenants={pageTenants}
          onClose={() => {
            setDialogOpen(false);
            setEditId(null);
          }}
        />
      )}
    </Box>
  );
}
